// The phone's LIVE screen inside the flow editor's screen window — the "Control" tab next
// to the inspector's snapshot. The author drives the phone here (open the app, reach the
// screen a step needs), then switches to the inspector to read that screen's elements.
//
// The picture comes from wherever the farm kind publishes it, exactly as the device cards
// do: a scrcpy session over the relay for host/aggregator phones (StreamSession — touch and
// keys go back through its control channel), the DuoPlus browser share in an <iframe> for
// duo, the GeeLark remote-access page for geelark. Nothing here is new transport; it is the
// card's screen area without the card.
//
// Coexists with the inspector's snapshot: scrcpy and `uiautomator dump` do not fight (the
// conflict the inspector documents is uiautomator vs a live Appium session), and the cloud
// embeds are on the provider's side entirely.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { isDuoFarm, isGeelarkFarm } from '../core/farm-kind';
import { StreamSession } from '../stream/stream-session';
import { lockIconSvg, phoneGlyphSvg } from '../stream/stream-controls-shared';
// Fixed layout size of the GeeLark remote-access page — see geelark-devices.ts, which
// owns the explanation; the numbers must stay in step with .glk-frame in style.css.
const GLK_FRAME_W = 395;
const GLK_FRAME_H = 690;
const spinnerSvg = '<svg class="duo-spin" width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M21 12a9 9 0 1 0-9 9"/></svg>';
const warningSvg = '<svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"/><path d="M12 9v4M12 17h.01"/></svg>';
function placeholder(icon, text) {
    return `<div class="device-player-placeholder"><span>${icon}</span><strong>${esc(text)}</strong></div>`;
}
/** A cloud phone's embed: fetch the provider's share/remote-access URL, frame it. */
function mountEmbed(host, d, path) {
    let alive = true;
    const glk = path === 'geelark-devices';
    const wrap = document.createElement('div');
    wrap.className = `fi-live__wrap${glk ? ' fi-live__wrap--glk' : ''}`;
    wrap.innerHTML = placeholder(spinnerSvg, t('farmdev.connecting'));
    host.replaceChildren(wrap);
    // The GeeLark page lays itself out at a fixed size; scale it to whatever the window
    // gives (contain fit), and again whenever the window is resized or dragged narrower.
    let observer = null;
    const fit = () => {
        const w = wrap.clientWidth, h = wrap.clientHeight;
        if (w && h)
            wrap.style.setProperty('--glk-zoom', String(Math.min(w / GLK_FRAME_W, h / GLK_FRAME_H)));
    };
    void (async () => {
        try {
            const r = await fetch(`/api/farms/${d.farmId}/${path}/${encodeURIComponent(d.udid)}/share`, { cache: 'no-store' });
            if (!alive)
                return;
            if (!r.ok) {
                const data = await r.json().catch(() => ({}));
                wrap.innerHTML = placeholder(warningSvg, data.error || t('farmdev.screen_unavailable', { status: r.status }));
                return;
            }
            const { url } = await r.json();
            if (!alive)
                return;
            wrap.innerHTML = `<iframe class="duo-screen-frame${glk ? ' glk-frame' : ''}" src="${esc(url)}" title="${esc(d.label)}"
                allow="clipboard-read; clipboard-write"
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"></iframe>`;
            if (glk && typeof ResizeObserver !== 'undefined') {
                fit();
                observer = new ResizeObserver(fit);
                observer.observe(wrap);
            }
        }
        catch {
            if (alive)
                wrap.innerHTML = placeholder(warningSvg, t('farmdev.network_error'));
        }
    })();
    return {
        stop() {
            alive = false;
            observer?.disconnect();
            host.replaceChildren();
        },
    };
}
/** A relay phone: scrcpy through the panel, with touch on the canvas and nav keys below. */
function mountScrcpy(host, d) {
    const stage = document.createElement('div');
    stage.className = 'device-stream-stage';
    stage.tabIndex = 0; // keyboard goes to the phone while the stage has focus
    const wrap = document.createElement('div');
    wrap.className = 'device-stream-canvas-wrap';
    const canvas = document.createElement('canvas');
    canvas.className = 'device-stream-canvas';
    canvas.width = 640;
    canvas.height = 360;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#111';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    wrap.appendChild(canvas);
    stage.appendChild(wrap);
    const nav = document.createElement('div');
    nav.className = 'fi-live__nav';
    nav.innerHTML = `
        <button type="button" class="fr-btn" data-keycode="4" title="${esc(t('common.back'))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 5-7 7 7 7"/></svg></button>
        <button type="button" class="fr-btn" data-keycode="3" title="${esc(t('stream.home'))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="7"/></svg></button>
        <button type="button" class="fr-btn" data-keycode="187" title="${esc(t('stream.recent_apps'))}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="5" y="5" width="14" height="14" rx="1.5"/></svg></button>`;
    host.replaceChildren(stage, nav);
    let session = new StreamSession(d.udid, canvas, ctx, () => { }, () => { }, (by) => {
        // Someone else took the phone. Say so where the picture was; the author can
        // switch to the inspector meanwhile, which needs no session.
        session?.close();
        session = null;
        wrap.innerHTML = placeholder(lockIconSvg, t('stream.in_use_by', { login: by }));
        nav.querySelectorAll('button').forEach((b) => { b.disabled = true; });
    });
    nav.querySelectorAll('[data-keycode]').forEach((b) => {
        b.addEventListener('click', () => session?.sendKeycode(Number(b.dataset['keycode'])));
    });
    return {
        stop() {
            session?.close();
            session = null;
            host.replaceChildren();
        },
    };
}
/** Show `d`'s live screen in `host`. Nothing to show without a device — the hint says so. */
export function mountLiveScreen(host, d) {
    if (!d) {
        host.innerHTML = `<div class="fi-live__wrap">${placeholder(phoneGlyphSvg, t('pf.live_hint'))}</div>`;
        return { stop() { host.replaceChildren(); } };
    }
    if (isDuoFarm(d.kind))
        return mountEmbed(host, d, 'duo-devices');
    if (isGeelarkFarm(d.kind))
        return mountEmbed(host, d, 'geelark-devices');
    return mountScrcpy(host, d);
}
